import React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import AutocompleteFormField from "components/autocomplete-form-field";
import {
  selectCurrentRegion,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useGetRegionsListQuery } from "services/regions";
import { Region } from "services/types/regions.type";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type SelectRegionProps = {
  label?: string;
  onOptionSelected?: (
    option:
      | { label: string; value: string }
      | { label: string; value: string }[]
      | null,
    regions: Region[]
  ) => void;
  required?: boolean;
  multiple?: boolean;
  openOnFocus?: boolean;
  autoFocus?: boolean;
  enablePortal?: boolean;
  skipFilteringByUser?: boolean;
  sx?: SxProps;
};

export type RhfSelectRegion<T extends FieldValues> = SelectRegionProps &
  UseControllerProps<T>;

export default function SelectRegion<T extends FieldValues>({
  control,
  label = "By Region",
  name,
  onOptionSelected,
  required = false,
  multiple = false,
  openOnFocus = false,
  enablePortal = false,
  autoFocus,
  sx,
  skipFilteringByUser
}: RhfSelectRegion<T>) {
  const currentRegion = useAppSelector(selectCurrentRegion);
  const currentUser = useAppSelector(selectCurrentUser);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: { label: string; value: string }) => option.label
  });

  const { data: regions = [], isLoading } = useGetRegionsListQuery(
    {
      query: "{id, name}",
      user: !skipFilteringByUser && currentRegion ? currentUser?.id : undefined
    },
    {
      skip: !currentUser?.id || currentUser?.id === -1
    }
  );

  return (
    <AutocompleteFormField
      enablePortal={enablePortal}
      openOnFocus={openOnFocus}
      autoHighlight
      autoFocus={autoFocus}
      name={name}
      multiple={multiple}
      label={label}
      isLoading={isLoading}
      required={required}
      filterOptions={filterOptions}
      options={regions.map((region) => ({
        label: region.name,
        value: String(region.id)
      }))}
      getOptionLabel={(option) => {
        return option?.label ?? "";
      }}
      renderOption={(props: object, option: any, { selected }: any) => (
        <ListItem {...props}>
          {multiple && (
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          <ListItemText>{option?.label}</ListItemText>
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onOptionSelected={(option) => {
        if (onOptionSelected) {
          requestAnimationFrame(() => {
            onOptionSelected(option, regions);
          });
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 220, ...sx }}
    />
  );
}
